import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import config from 'config';
import { toastFailure, toastSuccess } from 'modules/Notifications.js';
import auth0 from '../lib/Auth0';

// Size for server-side pagination. Arbitrarily large because we want *all*
// results, but the server-side endponts don't provide that option.
export const DEFAULT_SIZE = 100000;

// Augment fetchBaseQuery to show toast notifications on success or failure
// To show failure notifications, pass an extraOptions object with a subject
// and optional action. To show success notifications, pass an extraOptions
// object with both an action and a subject. The action should be a verb
// (e.g. 'create', 'update', 'delete') and the subject should be a noun.
const baseQuery = (baseQueryOptions) => async (args, api, extraOptions) => {
  const result = await fetchBaseQuery(baseQueryOptions)(args, api, extraOptions);
  const { subject, action, successMessage } = extraOptions ?? {};
  if (result.error) {
    if (subject) api.dispatch(toastFailure(`Failed to ${action ?? 'fetch'} ${subject}`));
  } else if (action && result?.meta?.response?.ok === true) {
    let actionPastTense;
    if (action === 'reset') {
      actionPastTense = action;
    } else if (action.endsWith('e')) {
      actionPastTense = `${action}d`;
    } else if (action.endsWith('y')) {
      actionPastTense = `${action.slice(0, -1)}ied`;
    } else {
      actionPastTense = `${action}ed`;
    }
    const message = successMessage ?? `Successfully ${actionPastTense} ${subject}`;
    api.dispatch(toastSuccess(message));
  }
  return result;
};

// Helper function for sorting an array of objects by a property
const sortObjectArrayByProp = (items = [], prop = '') => items.sort(
  (a, b) => a?.[prop]?.localeCompare(b?.[prop], undefined, { sensitivity: 'base' })
);

// Helpers for returning list of response items sorted by different properties
export const sortResponseItemsByName = (res) => sortObjectArrayByProp(res?.content ?? res, 'name');
export const sortResponseItemsByConnectionName = (res) => sortObjectArrayByProp(res?.content ?? res, 'connection_name');

export const genCacheKeyId = (...args) => args.join('\x1E');

export const apiSlice = createApi({
  baseQuery: baseQuery({
    baseUrl: config.api.url,
    prepareHeaders: async (headers) => {
      const token = await auth0.getAccessTokenSilently();
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    'Account',
    'AccountBrands',
    'Accounts',
    'AllBrandIntegrationsStatus',
    'AppUsers',
    'Audience',
    'Audiences',
    'AudienceAddressabilityChart',
    'AudienceAutoRefresh',
    'AudienceDestinations',
    'AudienceOnboardingStats',
    'AudienceOnboardingTasks',
    'AudienceSplitConfig',
    'AudienceSegments',
    'AudienceSnapshot',
    'BillableAudiences',
    'Brand',
    'BrandCategories',
    'BrandCustomProps',
    'BrandCustomPropVal',
    'BrandExplorerHomepage',
    'BrandExternalIdP',
    'BrandExternalIdPs',
    'BrandIntegrationConfig',
    'BrandIntegrationStatus',
    'BrandRetailers',
    'Brands',
    'BrandUsers',
    'BrandUsersToAdd',
    'Campaigns',
    'ExternalIdpConnections',
    'HexConnectionGroups',
    'IntegrationConnections',
    'IntegrationDefaultGroups',
    'SnowflakeConnectionRoles',
    'ThoughtSpotConnectionGroups',
    'User',
    'UserBrands',
    'UserIntegrationConfig',
    'UserSimpleSsoIntegrations',
    'UserSnowflakeRoleWarehouses',
    'Users',
  ],
  endpoints: (build) => ({
    // Fetch a custom property for a brand
    getBrandCustomProp: build.query({
      query: ({ brandId, propName }) => `/brands/${brandId}/properties/${propName}`,
    }),
  }),
});

export const {
  useGetBrandCustomPropQuery,
} = apiSlice;
